<template>
  <div class="w-full flex flex-wrap px-4 py-2">
    <div class="w-full text-right">
      <a class="cursor-pointer px-4 py-2" @click="$emit('close')">
        <font-awesome-icon :icon="['fas', 'times']" />
      </a>
    </div>

    <div class="w-1/2">
      <b-multiselect
        class="px-2"
        v-model="input.companies"
        :options="companies"
        label="name"
        track-by="slug"
        @input="form.errors.clear('companies')"
      ></b-multiselect>
      <p
        v-if="form.errors.has('companies')"
        class="text-sm text-red-800 mt-1 mb-0 mx-2"
      >
        {{ form.errors.first("companies") }}
      </p>
      <a class="text-green-700 pl-2">Manage Filters</a>
    </div>
    <div class="w-1/2">
      <div class="w-full px-2">
        <b-checkbox v-model="input.monthly" id="monthly" :errors="form.errors"
          >Monthly</b-checkbox
        >
      </div>
      <div class="w-full px-2" v-if="!input.monthly">
        <VueCtkDateTimePicker
          v-model="input.dateRange"
          :no-header="true"
          :no-button="true"
          :only-date="true"
          :range="true"
          formatted="MM/DD/YYYY"
          format="YYYY-MM-DD"
          label="Select Date Range"
          color="#2f855a"
          :auto-close="true"
          @input="
            () => {
              form.errors.clear('start_date')
              form.errors.clear('end_date')
            }
          "
        />
      </div>
      <p
        v-if="
          form.errors.has('monthly') ||
          form.errors.has('start_date') ||
          form.errors.has('end_date')
        "
        class="text-sm text-red-800 mt-1 mb-0 mx-2"
      >
        {{
          form.errors.first("monthly") ||
          form.errors.first("start_date") ||
          form.errors.first("end_date")
        }}
      </p>
    </div>

    <div class="w-full flex flex-wrap">
      <b-select
        class="mx-2 mb-1"
        style="width: 200px"
        v-model="input.campaignFilterOperator"
        id="campaignFilterOperator"
        label="Opterator"
        :errors="form.errors"
      >
        <option value="contains">Contains</option>
        <option value="does not contain">Does Not Contain</option>
      </b-select>
      <b-input
        class="mx-2 mb-1 flex-1"
        v-model="input.campaignFilter"
        id="campaignFilter"
        label="Campaign Filter"
        :errors="form.errors"
      ></b-input>
      <span class="w-full text-sm text-blue-800 mt-1 mb-2">
        If set, all data will be filtered to only campaigns that
        <strong>{{
          input.campaignFilterOperator === "contains"
            ? "contain"
            : "do not contain"
        }}</strong>
        this string.
      </span>
    </div>

    <div class="w-full flex">
      <p class="text-xl flex-1">
        <b-checkbox
          v-model="input.display.selected"
          id="display.selected"
          :errors="form.errors"
          @input="form.errors.clear('selected')"
          >Display</b-checkbox
        >
      </p>
    </div>

    <div class="w-full flex flex-wrap" v-if="input.display.selected">
      <div class="w-1/3">
        <b-select
          class="mx-2 mb-1"
          v-model="input.display.type"
          id="display.type"
          label="Type"
          :errors="form.errors"
        >
          <option value="cpm">CPM</option>
          <option value="agency">Agency %</option>
        </b-select>
      </div>

      <div class="w-1/3">
        <b-input
          class="mx-2 mb-1"
          min="0"
          v-model="input.display.percent"
          type="number"
          id="display.percent"
          :label="input.display.type === 'cpm' ? '% for Media' : 'Agency %'"
          :errors="form.errors"
        ></b-input>
      </div>

      <div class="w-1/3" v-show="input.display.type === 'cpm'">
        <b-input
          class="mx-2 mb-1"
          min="0"
          v-model="input.display.cpm"
          type="number"
          id="display.cpm"
          label="CPM"
          :errors="form.errors"
        ></b-input>
      </div>

      <div class="w-full">
        <label class="block mx-2 mb-1">
          <span class="cursor-pointer text-sm">Services</span>
          <b-multiselect
            v-model="input.display.services"
            :options="services.display"
            label="name"
            track-by="slug"
            @input="form.errors.clear('display.services')"
          ></b-multiselect>
        </label>
      </div>

      <div
        class="w-1/3"
        v-for="service in input.display.services"
        :key="service.slug"
      >
        <b-input
          class="mx-2 mb-1"
          min="0"
          max="100"
          v-model="input.display.budgets[service.slug]"
          type="number"
          :id="'display.budget.' + service.slug"
          :label="'Budget for ' + service.name"
          :errors="form.errors"
          @input="form.errors.clear('display')"
        ></b-input>
      </div>

      <div class="w-full" v-if="form.errors.has('display')">
        <p class="text-sm text-red-800 mb-1 mx-2">
          {{ form.errors.first("display") }}
        </p>
      </div>
    </div>

    <div class="w-full flex">
      <p class="text-xl flex-1">
        <b-checkbox
          v-model="input.video.selected"
          id="video.selected"
          :errors="form.errors"
          @input="form.errors.clear('selected')"
          >Video</b-checkbox
        >
      </p>
    </div>

    <div class="w-full flex flex-wrap" v-if="input.video.selected">
      <div class="w-1/3">
        <b-select
          class="mx-2 mb-1"
          v-model="input.video.type"
          id="video.type"
          label="Type"
          :errors="form.errors"
        >
          <option value="cpm">CPM</option>
          <option value="agency">Agency %</option>
        </b-select>
      </div>

      <div class="w-1/3">
        <b-input
          class="mx-2 mb-1"
          min="0"
          v-model="input.video.percent"
          type="number"
          id="video.percent"
          :label="input.video.type === 'cpm' ? '% for Media' : 'Agency %'"
          :errors="form.errors"
        ></b-input>
      </div>

      <div class="w-1/3" v-show="input.video.type === 'cpm'">
        <b-input
          class="mx-2 mb-1"
          min="0"
          v-model="input.video.cpm"
          type="number"
          id="video.cpm"
          label="CPM"
          :errors="form.errors"
        ></b-input>
      </div>

      <div class="w-full">
        <label class="block mx-2 mb-1">
          <span class="cursor-pointer text-sm">Services</span>
          <b-multiselect
            v-model="input.video.services"
            :options="services.video"
            label="name"
            track-by="slug"
            @input="form.errors.clear('video.services')"
          ></b-multiselect>
        </label>
      </div>

      <div
        class="w-1/3"
        v-for="service in input.video.services"
        :key="service.slug"
      >
        <b-input
          class="mx-2 mb-1"
          min="0"
          max="100"
          v-model="input.video.budgets[service.slug]"
          type="number"
          :id="'video.budget.' + service.slug"
          :label="'Budget for ' + service.name"
          :errors="form.errors"
          @input="form.errors.clear('video')"
        ></b-input>
      </div>

      <div class="w-full" v-if="form.errors.has('video')">
        <p class="text-sm text-red-800 mb-1 mx-2">
          {{ form.errors.first("video") }}
        </p>
      </div>
    </div>

    <div class="w-full flex">
      <p class="text-xl flex-1">
        <b-checkbox
          v-model="input.sem.selected"
          id="sem.selected"
          :errors="form.errors"
          @input="form.errors.clear('selected')"
          >SEM</b-checkbox
        >
      </p>
    </div>

    <div class="w-full flex flex-wrap" v-if="input.sem.selected">
      <div class="w-1/2">
        <b-input
          class="mx-2 mb-1"
          min="0"
          max="100"
          v-model="input.sem.percent"
          type="number"
          id="sem.percent"
          label="Agency %"
          :errors="form.errors"
        ></b-input>
      </div>

      <div class="w-1/2">
        <label class="block mx-2 mb-1">
          <span class="cursor-pointer text-sm">Services</span>
          <b-multiselect
            v-model="input.sem.services"
            :options="services.sem"
            label="name"
            track-by="slug"
            @input="form.errors.clear('sem.services')"
          ></b-multiselect>
        </label>
      </div>

      <div
        class="w-1/2"
        v-for="service in input.sem.services"
        :key="service.slug"
      >
        <b-input
          class="mx-2 mb-1"
          min="0"
          max="100"
          v-model="input.sem.budgets[service.slug]"
          type="number"
          :id="'sem.budget.' + service.slug"
          :label="'Budget for ' + service.name"
          :errors="form.errors"
          @input="form.errors.clear('sem')"
        ></b-input>
      </div>

      <div class="w-full" v-if="form.errors.has('sem')">
        <p class="text-sm text-red-800 mb-1 mx-2">
          {{ form.errors.first("sem") }}
        </p>
      </div>
    </div>

    <div class="w-full" v-if="form.errors.has('selected')">
      <p class="text-sm text-red-800 mb-1 mx-2">
        {{ form.errors.first("selected") }}
      </p>
    </div>

    <div class="w-full mt-2">
      <b-button @click="submit">Submit</b-button>
    </div>
  </div>
</template>

<script>
import { EventBus } from "../events"
import { map, cloneDeep } from "lodash"
import Form from "form-backend-validation"
import FormMixin from "@/components/FormMixin"

export default {
  name: "CreateForm",
  props: {
    companies: {
      required: true,
    },
  },
  mixins: [FormMixin],
  data() {
    return {
      form: new Form(),
      input: {
        companies: [],
        monthly: true,
        dateRange: {},
        campaignFilter: "",
        campaignFilterOperator: "contains",
        display: null,
        video: null,
        sem: null,
      },
    }
  },
  methods: {
    async submit() {
      EventBus.$emit("loading:start")
      let data = {
        monthly: this.input.monthly,
      }

      if (
        this.input.campaignFilter !== "" &&
        this.input.campaignFilter !== null
      ) {
        data.campaignFilter = this.input.campaignFilter
        data.campaignFilterOperator = this.input.campaignFilterOperator
      } else {
        data.campaignFilter = null
        data.campaignFilterOperator = null
      }

      if (this.input.display.selected) {
        data.display = cloneDeep(this.input.display)
        data.display.services = map(data.display.services, "slug")
      } else {
        data.display = null
      }

      if (this.input.video.selected) {
        data.video = cloneDeep(this.input.video)
        data.video.services = map(data.video.services, "slug")
      } else {
        data.video = null
      }

      if (this.input.sem.selected) {
        data.sem = cloneDeep(this.input.sem)
        data.sem.services = map(data.sem.services, "slug")
      } else {
        data.sem = null
      }

      data.companies = map(this.input.companies, "slug")
      if (this.input.monthly) {
        data.startDate = data.endDate = null
      } else {
        data.startDate = this.input.dateRange.start
        data.endDate = this.input.dateRange.end
      }

      await this.axios
        .post("budget/create", data)
        .then(() => {
          this.$emit("close")
          EventBus.$emit("fetch-settings")
        })
        .catch((resp) => {
          if (resp.response && resp.response.status === 422) {
            this.form.onFail(resp)
          }
        })

      EventBus.$emit("loading:stop")
    },
    pickerInput() {
      this.form.errors.clear("start_date")
      this.form.errors.clear("end_date")
    },
  },
  created() {
    this.input.display = cloneDeep(this.defaults.display)
    this.input.video = cloneDeep(this.defaults.video)
    this.input.sem = cloneDeep(this.defaults.sem)
  },
}
</script>

<style lang="scss" scoped>
</style>
